import * as React from 'react';
import { memo, useMemo } from 'react';

import { Chip, ChipProps } from '@column/column-ui-kit';
import { cn } from '@column/ui';

import { getProgressFromTransferStatus, getTransferProgressIcon } from '../progress';
import { formatTransferStatus, getTransferStatusTooltip } from '../status';
import { TransferProgress, TransferStatus, parseTransferStatus } from '../types';
import { TransferEntryType } from '~/repositories';

type ChipType = 'danger' | 'success' | 'info' | 'default';

interface TransferStatusChipProps {
  className?: string;
  status: string | TransferStatus;
  isReturn?: boolean;
  isIncoming?: boolean;
  returnPairTransferId?: string;
  size?: ChipProps['size'];
  transferType?: TransferEntryType;
}

const getChipType = (progress: TransferProgress, status: TransferStatus): ChipType => {
  if (progress === TransferProgress.PROGRESS_FAILED) return 'danger';
  if (progress === TransferProgress.PROGRESS_100) return 'success';

  const dangerStatuses = [
    TransferStatus.BLOCKED,
    TransferStatus.DENIED,
    TransferStatus.REJECTED,
    TransferStatus.RETURNED,
    TransferStatus.STOPPED,
  ];
  if (dangerStatuses.includes(status)) return 'danger';

  const successStatuses = [TransferStatus.APPROVED, TransferStatus.DEPOSITED, TransferStatus.COMPLETED];
  if (successStatuses.includes(status)) return 'success';

  if (status === TransferStatus.HOLD) return 'info';

  return 'default';
};

const getChipClassName = (chipType: ChipType) =>
  cn(
    '[&_svg]:[--icon-color:currentColor]',
    chipType === 'success' && '![--chip-color:var(--color-seafoam-600)]',
    chipType === 'danger' && '![--chip-color:var(--color-red-500)]',
    chipType === 'info' && '![--chip-color:var(--color-blue-500)]',
    chipType === 'default' && '![--chip-color:var(--color-muted-foreground)]'
  );

export const TransferStatusChip = memo<TransferStatusChipProps>(
  ({ className, size = 'default', status, isReturn, transferType }) => {
    const transferStatus = useMemo(() => parseTransferStatus(status), [status]);
    const transferProgress = useMemo(() => getProgressFromTransferStatus(transferStatus), [transferStatus]);
    const chipType = useMemo(() => getChipType(transferProgress, transferStatus), [transferProgress, transferStatus]);

    const displayText = useMemo(() => {
      if (isReturn) {
        return `Return ${formatTransferStatus(transferStatus)}`;
      }

      // Custom formatting for wire drawdown statuses
      if (transferType === 'wire-drawdown') {
        switch (transferStatus.toLowerCase()) {
          case 'approved':
            return 'Request Approved';
          case 'sent':
            return 'Request Sent';
          case 'initiated':
            return 'Request Initiated';
          case 'received':
            return 'Request Received';
          case 'rejected':
            return 'Request Rejected';
          case 'denied':
            return 'Request Denied';
          default:
            return formatTransferStatus(transferStatus);
        }
      }

      return formatTransferStatus(transferStatus);
    }, [isReturn, transferStatus, transferType]);

    return (
      <Chip
        className={cn(getChipClassName(chipType), className)}
        variant="subtle"
        icon={getTransferProgressIcon(transferProgress)}
        tooltip={getTransferStatusTooltip(transferStatus)}
        type={chipType}
        size={size}
      >
        {displayText}
      </Chip>
    );
  }
);
